// api.ts
// All API calls to your Lambda functions

import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { awsConfig } from "./aws-config";

// Gets the JWT token for the logged in user
export async function getAuthHeader() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return { Authorization: `Bearer ${token}` };
}

// Fresh ID token for WebSocket auth. fetchAuthSession refreshes expired
// tokens via the refresh token — unlike reading idToken from localStorage,
// which goes stale after an hour and makes wsConnect reject the connection.
export async function getWsToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() || "";
}

// ── Experts ───────────────────────────────────────────────

export async function getExperts(category?: string) {
  const headers = await getAuthHeader();
  const params = category ? { category } : {};
  const response = await axios.get(`${awsConfig.apiUrl}/experts`, {
    headers,
    params,
  });
  return response.data.experts;
}

// ── Bookings ──────────────────────────────────────────────

export async function getMyBookings() {
  const headers = await getAuthHeader();
  const response = await axios.get(`${awsConfig.apiUrl}/bookings/me`, {
    headers,
  });
  return response.data.bookings;
}

export async function createBooking(data: {
  proId: string;
  serviceType: string;
  scheduledDate: string;
  address: string;
  notes?: string;
  category?: string;
  jobSize?: string;
}) {
  const headers = await getAuthHeader();
  const response = await axios.post(`${awsConfig.apiUrl}/bookings`, data, {
    headers,
  });
  return response.data.booking;
}

export async function getProLeads() {
  const headers = await getAuthHeader();
  const response = await axios.get(
    `${awsConfig.apiUrl}/leads`,
    { headers }
  );
  return response.data.leads;
}

export async function updateBookingStatus(
  bookingId: string,
  createdAt: string,
  status: string,
  quoteAmount?: number
) {
  const headers = await getAuthHeader();
  const body: Record<string, any> = { bookingId, createdAt, status };
  if (quoteAmount !== undefined) body.quoteAmount = quoteAmount;
  const response = await axios.put(
    `${awsConfig.apiUrl}/bookings/status`,
    body,
    { headers }
  );
  return response.data;
}

export async function adminGetUsers() {
  const headers = await getAuthHeader();
  const response = await axios.get(
    `${awsConfig.apiUrl}/admin/users`,
    { headers }
  );
  return response.data.users;
}

export async function adminVerifyPro(proId: string, verified: boolean) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/admin/verify-pro`,
    { proId, verified },
    { headers }
  );
  return response.data;
}

export async function adminSuspendUser(username: string, suspend: boolean) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/admin/suspend-user`,
    { username, suspend },
    { headers }
  );
  return response.data;
}

export async function getUploadUrl(fileName: string, fileType: string, folder: string) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/upload-url`,
    { fileName, fileType, folder },
    { headers }
  );
  return response.data;
}

export async function uploadFileToS3(uploadUrl: string, file: File) {
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type }
  });
}

export async function getDownloadUrl(key: string) {
  const headers = await getAuthHeader();
  const response = await axios.get(`${awsConfig.apiUrl}/download-url`, {
    headers,
    params: { key }
  });
  return response.data.downloadUrl as string;
}

// Turns a stored full S3 URL (e.g. from getUploadUrl's fileUrl) back into
// the bare key that getDownloadUrl expects.
export function s3KeyFromUrl(url: string) {
  try {
    return decodeURIComponent(new URL(url).pathname.replace(/^\//, ""));
  } catch {
    return url;
  }
}

export async function updateProProfile(data: any) {
  const headers = await getAuthHeader();
  const response = await axios.put(`${awsConfig.apiUrl}/pro/profile`, data, { headers });
  return response.data;
}

export async function getProProfile() {
  const headers = await getAuthHeader();
  const response = await axios.get(
    `${awsConfig.apiUrl}/pro/profile`,
    { headers }
  );
  return response.data;
}

export async function createPaymentIntent(bookingId: string, createdAt: string, category: string, jobSize: string) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/payment-intent`,
    { bookingId, createdAt, category, jobSize },
    { headers }
  );
  return response.data;
}

export async function getLeadPricing(category?: string) {
  const response = await axios.get(
    `${awsConfig.apiUrl}/lead-pricing${category ? `?category=${category}` : ''}`
  );
  return response.data.pricing;
}

export async function updateLeadPricing(category: string, jobSize: string, price: number, description: string) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/lead-pricing`,
    { category, jobSize, price, description },
    { headers }
  );
  return response.data;
}

export async function setAvailability(availability: any) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/availability`,
    { availability },
    { headers }
  );
  return response.data;
}

export async function getAvailability(proId: string) {
  const response = await axios.get(
    `${awsConfig.apiUrl}/availability?proId=${proId}`
  );
  return response.data.availability;
}

export async function createReview(proId: string, rating: number, review: string, bookingId: string, tags?: string[], photoUrls?: string[]) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/reviews`,
    { proId, rating, review, bookingId, tags, photoUrls },
    { headers }
  );
  return response.data;
}

export async function voteReviewHelpful(reviewId: string) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/reviews/vote`,
    { reviewId },
    { headers }
  );
  return response.data;
}

export async function getReviews(proId: string) {
  const response = await axios.get(
    `${awsConfig.apiUrl}/reviews?proId=${proId}`
  );
  return response.data.reviews;
}

export async function respondToReview(reviewId: string, response: string) {
  const headers = await getAuthHeader();
  const res = await axios.put(
    `${awsConfig.apiUrl}/reviews/respond`,
    { reviewId, response },
    { headers }
  );
  return res.data;
}

export async function getPortfolio(proId: string) {
  const response = await axios.get(
    `${awsConfig.apiUrl}/portfolio?proId=${proId}`
  );
  return response.data.portfolio;
}

export async function updatePortfolio(portfolio: any[]) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/portfolio`,
    { portfolio },
    { headers }
  );
  return response.data;
}

export async function createDispute(bookingId: string, reason: string, description: string, disputeType: string) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/disputes`,
    { bookingId, reason, description, disputeType },
    { headers }
  );
  return response.data;
}

export async function getDisputes() {
  const headers = await getAuthHeader();
  const response = await axios.get(
    `${awsConfig.apiUrl}/disputes`,
    { headers }
  );
  return response.data.disputes;
}

export async function resolveDispute(disputeId: string, resolution: string, status: string, adminNotes: string) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/disputes/resolve`,
    { disputeId, resolution, status, adminNotes },
    { headers }
  );
  return response.data;
}

export async function createTicket(bookingId: string, proId: string, issueType: string, description: string, evidenceUrls: string[]) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/tickets`,
    { bookingId, proId, issueType, description, evidenceUrls },
    { headers }
  );
  return response.data;
}

export async function getTickets() {
  const headers = await getAuthHeader();
  const response = await axios.get(
    `${awsConfig.apiUrl}/tickets`,
    { headers }
  );
  return response.data.tickets;
}

export async function updateTicket(ticketId: string, data: any) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/tickets`,
    { ticketId, ...data },
    { headers }
  );
  return response.data;
}

export async function updateOnlineStatus(isOnline: boolean) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/pro/online-status`,
    { isOnline },
    { headers }
  );
  return response.data;
}

export async function dispatchLead(data: {
  category: string;
  jobSize: string;
  description: string;
  address: string;
  scheduledDate: string;
  photoUrls?: string[];
  privateNotes?: string;
  specificProId?: string;
  urgency?: string;
  lat?: number;
  lng?: number;
  budget?: number;
}) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/dispatch`,
    data,
    { headers }
  );
  return response.data;
}

export async function savePushSubscription(subscription: PushSubscription) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/push/subscribe`,
    { subscription },
    { headers }
  );
  return response.data;
}

export async function getAnalytics() {
  const headers = await getAuthHeader();
  const response = await axios.get(
    `${awsConfig.apiUrl}/analytics`,
    { headers }
  );
  return response.data;
}


export async function addService(name: string, icon: string, subcategories: string[]) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/services`,
    { name, icon, subcategories },
    { headers }
  );
  return response.data;
}

export async function updateService(categoryId: string, name: string, icon: string, subcategories: string[], active: boolean) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/services`,
    { categoryId, name, icon, subcategories, active },
    { headers }
  );
  return response.data;
}

export async function deleteService(categoryId: string) {
  const headers = await getAuthHeader();
  const response = await axios.delete(
    `${awsConfig.apiUrl}/services`,
    { headers, data: { categoryId } }
  );
  return response.data;
}

export async function getZipCodes(city: string = "Houston") {
  const headers = await getAuthHeader();
  const response = await axios.get(
    `${awsConfig.apiUrl}/zip-codes?city=${city}`,
    { headers }
  );
  return response.data.zipCodes;
}

export async function updateServiceArea(serviceZipCodes: string[]) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${awsConfig.apiUrl}/pro/service-area`,
    { serviceZipCodes },
    { headers }
  );
  return response.data;
}

export async function getAdminFlags() {
  const headers = await getAuthHeader();
  const response = await axios.get(
    `${awsConfig.apiUrl}/admin/flags`,
    { headers }
  );
  return response.data.flags;
}

export async function adminFlagAction(flagId: string, proId: string, action: string, note: string) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${awsConfig.apiUrl}/admin/flags`,
    { flagId, proId, action, note },
    { headers }
  );
  return response.data;
}

// ── Services & Categories (PUBLIC — no auth required) ────

export interface Service {
  categoryId: string;
  name: string;
  icon?: string;
  subcategories?: string[];
  featured?: boolean;
  displayOrder?: number;
  active?: boolean;
}

export interface VirtualCategorySubRef {
  subcategoryName: string;
  categoryId: string;
  categoryName: string;
}

export interface VirtualCategory {
  virtualCategoryId: string;
  name: string;
  icon?: string;
  description?: string;
  featured?: boolean;
  displayOrder?: number;
  subcategoryRefs?: VirtualCategorySubRef[];
  active?: boolean;
}

export async function getServices(): Promise<Service[]> {
  const response = await axios.get(`${awsConfig.apiUrl}/services`);
  return response.data.services || [];
}

export async function getVirtualCategories(): Promise<VirtualCategory[]> {
  const response = await axios.get(`${awsConfig.apiUrl}/virtual-categories`);
  return response.data.virtualCategories || [];
}

// Helper: convert a category name to a URL-safe slug
export function categoryNameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\//g, "-")
    .replace(/,/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ── Pros by service + ZIP (PUBLIC) ────────────────────────

export interface ProSummary {
  proId: string;
  name: string;
  businessName: string;
  photoUrl: string;
  serviceCategory: string;
  subcategories: string[];
  rating: number;
  reviewCount: number;
  bio: string;
  city: string;
  hourlyRate: number;
  yearsInBusiness: string;
  verified: boolean;
}

export interface ProsByServiceResponse {
  inArea: ProSummary[];
  otherInCity: ProSummary[];
  matchType: "subcategory" | "category";
  zip: string | null;
  subcategory: string | null;
  category: string | null;
}

export async function getProsByService(args: {
  subcategory?: string;
  category?: string;
  zip?: string;
}): Promise<ProsByServiceResponse> {
  const params: Record<string, string> = {};
  if (args.subcategory) params.subcategory = args.subcategory;
  if (args.category) params.category = args.category;
  if (args.zip) params.zip = args.zip;
  const response = await axios.get(
    `${awsConfig.apiUrl}/public/pros-by-service`,
    { params }
  );
  return response.data;
}

// ── Saved Addresses (authed) ──────────────────────────────

export interface SavedAddress {
  addressId: string;
  userId?: string;
  label: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  notes?: string;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export async function getMyAddresses(): Promise<SavedAddress[]> {
  const headers = await getAuthHeader();
  const response = await axios.get(`${awsConfig.apiUrl}/addresses`, { headers });
  return response.data.addresses || [];
}

export async function saveAddress(
  address: Omit<SavedAddress, "addressId" | "userId" | "createdAt" | "updatedAt"> & { addressId?: string }
): Promise<SavedAddress> {
  const headers = await getAuthHeader();
  const response = await axios.post(`${awsConfig.apiUrl}/addresses`, address, { headers });
  return response.data.address;
}

export async function deleteAddress(addressId: string): Promise<void> {
  const headers = await getAuthHeader();
  await axios.delete(`${awsConfig.apiUrl}/addresses/${encodeURIComponent(addressId)}`, { headers });
}

// ── Account ───────────────────────────────────────────────

// Permanently deletes the signed-in user's account and personal data.
export async function deleteAccount(): Promise<void> {
  const headers = await getAuthHeader();
  await axios.delete(`${awsConfig.apiUrl}/account`, { headers });
}

// ── Conversations ─────────────────────────────────────────

export interface ConversationSummary {
  conversationId: string;
  otherUserId: string;
  otherUserName: string;
  lastMessage: string;
  lastSentAt: string;
  lastSenderId: string;
}

// Lists the signed-in user's conversations (works for clients and pros).
export async function getMyConversations(): Promise<ConversationSummary[]> {
  const headers = await getAuthHeader();
  const response = await axios.get(`${awsConfig.apiUrl}/conversations`, { headers });
  return response.data.conversations || [];
}

// ── Moderation ────────────────────────────────────────────

export type ReportTargetType = "pro" | "review" | "message" | "user";

// Reports content or a user; lands in the admin flags queue.
export async function reportContent(targetType: ReportTargetType, targetId: string, reason: string, comment?: string) {
  const headers = await getAuthHeader();
  await axios.post(`${awsConfig.apiUrl}/reports`, { targetType, targetId, reason, comment }, { headers });
}

// Blocks or unblocks a user. Blocked users' messages are dropped and their
// conversations disappear from the inbox.
export async function blockUser(userId: string, action: "block" | "unblock" = "block"): Promise<string[]> {
  const headers = await getAuthHeader();
  const response = await axios.post(`${awsConfig.apiUrl}/block`, { userId, action }, { headers });
  return response.data.blockedUsers || [];
}

// Lists the signed-in user's blocked users with display names.
export async function getBlockedUsers(): Promise<Array<{ userId: string; name: string }>> {
  const headers = await getAuthHeader();
  const response = await axios.get(`${awsConfig.apiUrl}/block`, { headers });
  return response.data.blockedUsers || [];
}
